import { useEffect, useState } from 'react';

const REFRESH_MS = 60000;

async function fetchDex(pair) {
  const r = await fetch(`https://api.dexscreener.com/latest/dex/pairs/bsc/${pair}`);
  if (!r.ok) throw new Error(`dexscreener ${r.status}`);
  const j = await r.json();
  const p = j.pair || (j.pairs && j.pairs[0]);
  if (!p) throw new Error('no pair');
  return {
    marketCap: typeof p.marketCap === 'number' ? p.marketCap : p.fdv ?? null,
    volume24h: p.volume?.h24 ?? null,
    liquidityUsd: p.liquidity?.usd ?? null,
    liquidityQuote: p.liquidity?.quote ?? null,
    priceUsd: p.priceUsd ? parseFloat(p.priceUsd) : null,
    priceChange24h: p.priceChange?.h24 ?? null,
  };
}

async function fetchHolders(addr) {
  const r = await fetch(
    `https://api.gopluslabs.io/api/v1/token_security/56?contract_addresses=${addr}`
  );
  if (!r.ok) throw new Error(`goplus ${r.status}`);
  const j = await r.json();
  const t = j.result?.[addr.toLowerCase()];
  if (!t) return null;
  const n = parseInt(t.holder_count, 10);
  return Number.isFinite(n) ? n : null;
}

export function useMarketStats({ pair, contract }) {
  const [stats, setStats] = useState({
    marketCap: null,
    volume24h: null,
    liquidityUsd: null,
    liquidityQuote: null,
    priceUsd: null,
    priceChange24h: null,
    holders: null,
    loading: true,
    updatedAt: null,
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const [dexResult, holdersResult] = await Promise.allSettled([
        fetchDex(pair),
        fetchHolders(contract),
      ]);
      if (cancelled) return;

      const dex = dexResult.status === 'fulfilled' ? dexResult.value : null;
      const holders =
        holdersResult.status === 'fulfilled' ? holdersResult.value : null;

      setStats({
        marketCap: dex?.marketCap ?? null,
        volume24h: dex?.volume24h ?? null,
        liquidityUsd: dex?.liquidityUsd ?? null,
        liquidityQuote: dex?.liquidityQuote ?? null,
        priceUsd: dex?.priceUsd ?? null,
        priceChange24h: dex?.priceChange24h ?? null,
        holders,
        loading: false,
        updatedAt: Date.now(),
      });
    };

    load();
    const id = setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [pair, contract]);

  return stats;
}

export const fmtUsd = (n) => {
  if (n == null || !Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
};

export const fmtNum = (n) => {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US');
};

export const fmtPct = (n) => {
  if (n == null || !Number.isFinite(n)) return null;
  const arrow = n >= 0 ? '↗' : '↘';
  const sign = n >= 0 ? '+' : '';
  return `${arrow} ${sign}${n.toFixed(2)}%`;
};

export const fmtPrice = (n) => {
  if (n == null || !Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1) return `$${n.toFixed(4)}`;
  if (Math.abs(n) >= 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toPrecision(4)}`;
};
